

/**
 * Product scanner — extracts one or more purchasable products from the
 * current page DOM so the "Recent Purchase" notifications can reference
 * real products and prices. Uses a layered strategy with fallbacks:
 *
 *   1. Explicit opt-in markup ([data-purchase-product])
 *   2. JSON-LD (schema.org Product / Offer)
 *   3. Generic ecommerce heuristics (CTA + price + title + image)
 *   4. A hardcoded fallback distribution (70% physical $27 / 30% digital $17)
 */

export type ScannedProduct = {
  title: string
  editionLabel: string
  priceNumber: number
  priceText: string
  thumbnailUrl: string
  /** Relative pick weight — used by the fallback distribution. */
  weight?: number
}

const PRICE_REGEX = /(?:USD\s*)?\$\s?(\d{1,4}(?:[.,]\d{2})?)|(\d{1,4}(?:[.,]\d{2})?)\s?(?:USD|usd)/

const CTA_KEYWORDS = [
  'add to cart',
  'buy',
  'checkout',
  'order',
  'purchase',
  'comprar',
  'añadir al carrito',
  'agregar al carrito',
  'quiero',
  'acceso',
]

const FALLBACK_PRODUCTS: ScannedProduct[] = [
  {
    title: 'Physical Book',
    editionLabel: 'Pack Despertar',
    priceNumber: 27,
    priceText: '$27',
    thumbnailUrl: '',
    weight: 70,
  },
  {
    title: 'Digital Edition',
    editionLabel: 'Edición Digital',
    priceNumber: 17,
    priceText: '$17',
    thumbnailUrl: '',
    weight: 30,
  },
]

function parsePrice(text: string): { number: number; text: string } | null {
  if (!text) return null
  const match = text.match(PRICE_REGEX)
  if (!match) return null
  const raw = (match[1] || match[2] || '').replace(',', '.')
  const num = parseFloat(raw)
  if (Number.isNaN(num) || num <= 0) return null
  return { number: num, text: match[0].trim() }
}

function absoluteUrl(src: string | null | undefined): string {
  if (!src) return ''
  try {
    return new URL(src, window.location.href).href
  } catch {
    return src
  }
}

/** 1) Explicit opt-in markup — the most reliable source. */
function scanDataAttributes(): ScannedProduct[] {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-purchase-product]'))
  const products: ScannedProduct[] = []
  for (const node of nodes) {
    const title = node.dataset.productTitle?.trim() || ''
    const priceAttr = node.dataset.productPrice?.trim() || ''
    const parsed = parsePrice(priceAttr)
    if (!title || !parsed) continue
    let thumb = node.dataset.productThumbnail?.trim() || ''
    if (!thumb) {
      const img = node.querySelector('img')
      thumb = img ? absoluteUrl(img.getAttribute('src')) : ''
    }
    products.push({
      title,
      editionLabel: node.dataset.productEdition?.trim() || title,
      priceNumber: parsed.number,
      priceText: priceAttr || parsed.text,
      thumbnailUrl: absoluteUrl(thumb),
    })
  }
  return products
}

/** 2) JSON-LD structured data (schema.org Product / Offer). */
function scanJsonLd(): ScannedProduct[] {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]'),
  )
  const products: ScannedProduct[] = []

  const handleNode = (node: any) => {
    if (!node || typeof node !== 'object') return
    const type = node['@type']
    const isProduct = type === 'Product' || (Array.isArray(type) && type.includes('Product'))
    if (!isProduct) return
    const offer = Array.isArray(node.offers) ? node.offers[0] : node.offers
    const priceRaw = offer?.price ?? offer?.lowPrice
    const priceNumber = priceRaw != null ? parseFloat(String(priceRaw)) : NaN
    if (!node.name || Number.isNaN(priceNumber) || priceNumber <= 0) return
    const currency = offer?.priceCurrency === 'USD' ? '$' : offer?.priceCurrency || '$'
    const image = Array.isArray(node.image) ? node.image[0] : node.image
    products.push({
      title: String(node.name),
      editionLabel: node.category ? String(node.category) : String(node.name),
      priceNumber,
      priceText: `${currency}${priceNumber.toFixed(2).replace(/\.00$/, '')}`,
      thumbnailUrl: absoluteUrl(image),
    })
  }

  for (const script of scripts) {
    try {
      const data = JSON.parse(script.textContent || '')
      const list = Array.isArray(data) ? data : data['@graph'] ? data['@graph'] : [data]
      list.forEach(handleNode)
    } catch {
      // ignore malformed JSON-LD
    }
  }
  return products
}

/** 3) Generic heuristics: find CTA-bearing sections with a price + title + image. */
function scanHeuristics(): ScannedProduct[] {
  const products: ScannedProduct[] = []
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('section, article, [class*="card"]'))

  for (const el of candidates) {
    const text = (el.textContent || '').toLowerCase()
    const hasCta = CTA_KEYWORDS.some((kw) => text.includes(kw))
    if (!hasCta) continue

    const parsed = parsePrice(el.textContent || '')
    if (!parsed) continue

    const heading = el.querySelector('h1, h2, h3, h4')
    const title = heading?.textContent?.trim().slice(0, 80)
    if (!title) continue

    const img = el.querySelector('img')
    products.push({
      title,
      editionLabel: title,
      priceNumber: parsed.number,
      priceText: parsed.text,
      thumbnailUrl: img ? absoluteUrl(img.getAttribute('src')) : '',
    })
    // One product per matched section is enough to avoid noise.
    if (products.length >= 4) break
  }
  return products
}

/**
 * Runs the full scan and returns a de-duplicated product catalog.
 * Always returns at least one product (falls back if nothing detected).
 */
export function scanProducts(): ScannedProduct[] {
  let found: ScannedProduct[] = []

  try {
    found = scanDataAttributes()
    if (found.length === 0) found = scanJsonLd()
    if (found.length === 0) found = scanHeuristics()
  } catch {
    found = []
  }

  // De-duplicate by title + price.
  const seen = new Set<string>()
  const unique = found.filter((p) => {
    const key = `${p.title}::${p.priceNumber}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return unique.length > 0 ? unique : FALLBACK_PRODUCTS
}

/** Weighted random pick from a catalog (honors optional `weight`). */
export function pickProduct(catalog: ScannedProduct[]): ScannedProduct {
  if (catalog.length === 1) return catalog[0]
  const total = catalog.reduce((sum, p) => sum + (p.weight ?? 1), 0)
  let roll = Math.random() * total
  for (const product of catalog) {
    roll -= product.weight ?? 1
    if (roll <= 0) return product
  }
  return catalog[catalog.length - 1]
}

/** Formats a price fallback when priceText is missing. */
export function formatPrice(product: ScannedProduct): string {
  if (product.priceText) return product.priceText
  const n = product.priceNumber
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`
}


